/**
 * GeneralTabShareholderCard Component
 *
 * Renders the Shareholder Structure card for the General tab, displaying:
 * - Ultimate shareholders table (share %, name, age)
 * - Loading and error states
 * - Independent company indicator
 *
 * CALCULATION OWNERSHIP: This component receives all data from parent.
 * Share percentage formatting uses shared utility from shareholderGraphUtils.
 */

import PropTypes from 'prop-types';
import GeneralLoader from '../../../loaders/GeneralLoader';
import { ICON_BUTTON_ICONS } from '../../../buttons/IconButton';
import { FIELD_SOURCE, dataSourceClass } from '../../../../utils/constants/dataSources';
import { formatBeneficialOwnership } from '../ShareholderStructure/shareholderGraphUtils';
import OwnershipSummaryPanel from '../../ownership/OwnershipSummaryPanel';

/**
 * @param {Object} props
 * @param {string} props.companyName - Company name for subtitle
 * @param {Array} props.ultimateShareholders - Array of shareholder entries
 * @param {boolean} props.isLoadingDetails - Whether details are loading
 * @param {Error|null} props.loadDetailsError - Error from loading details
 * @param {boolean} props.isIndependent - Whether company has no majority shareholder
 * @param {boolean} props.canViewExtendedData - Whether user can view extended data
 * @param {Object|null} props.ownershipSummary - Per-basis ownership figures from details
 * @param {Object|null} props.ownershipMatch - Search basis and presets when opened from a search
 * @param {string} [props.companyId] - Company shown; resets the ownership panel when it changes
 */
const GeneralTabShareholderCard = ({
  companyName = '',
  ultimateShareholders = [],
  isLoadingDetails = false,
  loadDetailsError = null,
  isIndependent = false,
  canViewExtendedData = true,
  ownershipSummary = null,
  ownershipMatch = null,
  companyId = undefined
}) => {
  // Only render if user can view extended data
  if (!canViewExtendedData) {
    return null;
  }

  return (
    <div className="card shareholder">
      <div className={`title ${dataSourceClass(FIELD_SOURCE.shareholders)}`}>
        Shareholder structure
      </div>
      {!!ownershipSummary && (
        // Keyed by company: the panel keeps the user's basis pick, which must not follow
        // an overlay navigation to another company
        <OwnershipSummaryPanel key={companyId} summary={ownershipSummary} match={ownershipMatch} />
      )}
      <div className="sub-title">Ultimate owner(s) of {companyName}</div>

      {/* Loading State */}
      {!!isLoadingDetails && (
        <div className="shareholder-flat-structure-wrap">
          <GeneralLoader />
        </div>
      )}

      {/* Error State */}
      {!!loadDetailsError && (
        <div className="shareholder-flat-structure-wrap">
          <div className="load-fail-icon">
            <img src={ICON_BUTTON_ICONS.EXCLAMATAION_TRIANGLE_FILL} alt="" />
          </div>
        </div>
      )}

      {/* Shareholders Table - only show when not loading and no error */}
      {!isLoadingDetails && !loadDetailsError && !!ultimateShareholders?.length && (
        <div className="shareholder-flat-structure-wrap">
          <div className="ultimate-header">
            <div className="item share">Share (%)</div>
            <div className="item name">Name</div>
            <div className="item age">Age</div>
          </div>
          {ultimateShareholders.map((entry, idx) => {
            return (
              <div
                className="ultimate-row"
                key={entry.member_id || entry.member_name}
                style={idx === 0 ? { marginTop: '8px' } : {}}
              >
                <div className="item share">{formatBeneficialOwnership(entry)}</div>
                <div className="item name">{entry.member_name}</div>
                {!!entry.age && <div className="item age">{entry.age}</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* Independent Company Indicator */}
      {isIndependent && (
        <div className="independent-info-wrapper">
          <div className="independent-info-icon-wrap">
            <img src={ICON_BUTTON_ICONS.INFO_CIRCLE} alt="" />
          </div>
          <div className="independent-info-text">
            {`${companyName} has no direct majority shareholder of >50.0%`}
          </div>
        </div>
      )}
    </div>
  );
};

GeneralTabShareholderCard.propTypes = {
  companyName: PropTypes.string,
  ultimateShareholders: PropTypes.arrayOf(PropTypes.object),
  isLoadingDetails: PropTypes.bool,
  loadDetailsError: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  isIndependent: PropTypes.bool,
  canViewExtendedData: PropTypes.bool,
  ownershipSummary: PropTypes.object,
  companyId: PropTypes.string,
  ownershipMatch: PropTypes.shape({
    basis: PropTypes.string,
    structures: PropTypes.arrayOf(PropTypes.string)
  })
};

export default GeneralTabShareholderCard;
